import clsx from "clsx";
import React, { useState } from "react";
import { FieldValues } from "react-hook-form";

import { FormField } from "@/components/Interface/FormFields/FormField/FormField.tsx";
import { FormFieldProps } from "@/components/Interface/FormFields/FormField/FormField.utils.ts";
import { getNestedValue } from "@/utils/objectUtils";

export interface FormFieldWithHintProps<TFormValues extends FieldValues>
  extends FormFieldProps<TFormValues> {
  hint?: string;
}

export const FormFieldWithHint = <TFormValues extends object>(
  props: React.PropsWithChildren<FormFieldWithHintProps<TFormValues>>,
) => {
  const [selected, setSelected] = useState(false);

  // Use getNestedValue to safely access nested field errors
  const fieldError = getNestedValue(props.errors, props.name);
  const hasError = fieldError !== undefined;
  const hasErrorMessage = !!fieldError?.message;

  const formField = (
    <FormField
      {...props}
      options={{
        ...props.options,
        onBlur: props.options?.readOnly ? undefined : () => setSelected(false),
      }}
      // Disabled fields should not be focusable
      {...(props.options?.readOnly
        ? undefined
        : { onFocus: () => setSelected(true) })}
    >
      {props.children}
    </FormField>
  );

  // Stacked mode: FormField owns the grey box and renders error outside it.
  // This outer wrapper must NOT carry form-field-container so it doesn't
  // trigger a second grey box via the stacked CSS selector.
  if (props.layout === "stacked") {
    return (
      <div>
        {formField}
        {props.hint && selected && !hasErrorMessage && (
          <span className="hint">{props.hint}</span>
        )}
      </div>
    );
  }

  return (
    <div className={clsx("form-field-container", hasError && "invalid")}>
      {formField}
      {props.hint && selected && !hasErrorMessage && (
        <span className="hint">{props.hint}</span>
      )}
    </div>
  );
};
